import React, { useState, useEffect, useRef } from 'react';
import { primary } from "shared/theme/trodoColors";
import Sidebar from "shared/components/layout/Sidebar";
import GlobalTopBar from "shared/components/layout/GlobalTopBar";
import { BreadcrumbProvider } from "shared/context/BreadcrumbContext";

import Settings from "features/settings/pages/Settings";
import UserSettings from "features/settings/pages/UserSettings";
import { Menu, BarChart } from "lucide-react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import InboxPage from 'features/dashboard/pages/InboxPage'
import ActivityPage from 'features/activity/pages/Activity'
import UserProfileRoute from 'features/users/UserProfileRoute'
import UserProfileHost from 'features/users/UserProfileHost'
import GroupDetailPage from 'features/activity/pages/GroupDetailPage'
import AgentRunsPage from 'features/agent-analytics/pages/AgentRunsPage'
import ConversationsPage from 'features/agent-analytics/pages/ConversationsPage'
import TracesPage from 'features/agent-analytics/pages/TracesPage'
import CapabilitiesPage from 'features/agent-analytics/pages/CapabilitiesPage'
import IssuesPage from 'features/agent-analytics/pages/IssuesPage'
import Evals3Page from 'features/evals3/pages/Evals3Page'
import { ISSUES_UI_ENABLED } from 'shared/config/featureFlags'
import AgentsPage from 'features/agents/pages/AgentsPage'
import PlaygroundPage from 'features/playground/pages/PlaygroundPage'
import PromptsPage from 'features/prompts/pages/PromptsPage'
import DatasetsPage from 'features/datasets/pages/DatasetsPage'

import ChatPage from 'features/dashboard/pages/ChatPage'
// Removed accidentally-committed import of features/search/pages/SemanticSearchPage
// — that file is in your local working tree but not in git, which broke the
// Vercel build. Restore this import locally when the semantic-search feature
// is ready to merge.
import SubscriptionRequired from "shared/components/subscription/SubscriptionRequired";
import { PinnedBoardsProvider } from "features/analytics/studio/context/PinnedBoardsContext";
import InstallPanel from "features/onboarding/components/InstallPanel";
import useTeamActivation from "shared/hooks/useTeamActivation";
import AskPanel from "features/dashboard/askPanel/AskPanel";
import { SIDE_PANEL_SLOT_ID } from "features/dashboard/chatSessions";
import AlertsPage from "features/alerts/pages/AlertsPage";
import { PageLoader } from "shared/components/ui/BrandLoader";
import DashboardsPage, { OverviewPage } from "features/dashboards/pages/DashboardsPage";
import { LEGACY_STUDIO_UI } from "shared/config/featureFlags";

/** What a page's loader says while it has nothing to show: what is coming, not "Loading". */
const PAGE_LOADING_LABEL = {
  dashboard: "Loading your dashboard",
  activity: "Loading activity",
  "agent-analytics-overview": "Loading the overview",
  reports: "Loading reports",
  overview: "Loading the overview",
  "agent-analytics-runs": "Loading runs",
  "agent-analytics-traces": "Loading traces",
  "agent-analytics-capabilities": "Loading capabilities",
  "agent-analytics-issues": "Loading issues",
  "agent-analytics-issues-catalog": "Loading issues",
  "agent-analytics-evals": "Loading evals",
  agents: "Loading agents",
  inbox: "Loading your inbox",
};

// The feature is named "Capabilities". 'use-cases' (the previous name) and
// 'clusters' (the page before that) are kept for backward-compat with any
// bookmarks/links — the route handler below redirects both to 'capabilities'
// on the way in so stale URLs still land on the right page.
const AGENT_ANALYTICS_SUBPAGES = new Set(['overview', 'runs', 'traces', 'conversations', 'clusters', 'use-cases', 'capabilities', 'issues', 'evals'])

/**
 * Pages that stay fully usable before a team has instrumented anything.
 *
 * Settings is the one that matters: a team that has not sent a trace yet still
 * needs to invite the developer who will do the wiring, and still needs to be
 * able to upgrade to Pro. Gating billing behind instrumentation put the payment
 * step after the step customers get stuck on.
 */
const INSTALL_EXEMPT_PAGES = new Set(['settings', 'user-settings']);

const ASK_BESIDE_KEY = 'trodo:askBeside';

const Dashboard = () => {
  const params = useParams();
  const teamIdFromUrl = params.team;
  // State management
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  /**
   * Ask panel visibility. Owned here rather than in the sidebar because the
   * panel is a sibling of the page content — it has to shrink the column, and
   * the sidebar cannot reach outside itself to do that. Opened by the sidebar's
   * Ask item, by Ctrl+I, and by the Ask button on a run or span.
   */
  const [askOpen, setAskOpen] = useState(false);
  const sidebarRef = useRef(null);
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => document.documentElement.style.setProperty('--trodo-rail-w', `${el.getBoundingClientRect().width}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--trodo-rail-w'); };
  }, []);
  // `trodo:ask:open` may carry a drafted message — { prompt, send } — from a
  // page that knows what to ask ("Create this eval with Ask"). It opens a
  // fresh conversation with it in the composer, or sent when `send` is set.
  const [askDraft, setAskDraft] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      setAskOpen(true);
      const d = e && e.detail;
      // Kept as drafted — a trailing space is where the person carries on typing.
      if (d && typeof d.prompt === 'string' && d.prompt.trim()) setAskDraft({ prompt: d.send ? d.prompt.trim() : d.prompt, send: Boolean(d.send), nonce: Date.now() });
    };
    const onToggle = () => setAskOpen((v) => !v);
    window.addEventListener('trodo:ask:open', onOpen);
    window.addEventListener('trodo:ask:toggle', onToggle);
    return () => {
      window.removeEventListener('trodo:ask:open', onOpen);
      window.removeEventListener('trodo:ask:toggle', onToggle);
    };
  }, []);
  const [askResume, setAskResume] = useState(null);
  // A conversation handed over by the chat page as it opened another page beside it.
  useEffect(() => {
    let handed = null;
    try { handed = JSON.parse(sessionStorage.getItem(ASK_BESIDE_KEY) || 'null'); sessionStorage.removeItem(ASK_BESIDE_KEY); } catch { handed = null; }
    if (!handed || Date.now() - Number(handed.at || 0) > 15000) return;
    if (handed.sessionId) setAskResume({ sessionId: handed.sessionId, nonce: Date.now() });
    setAskOpen(true);
  }, []);
  const [selectedPage, setSelectedPage] = useState("dashboard");
  const [isPageLoading, setIsPageLoading] = useState(true); // Track if page is being determined from URL
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false
  });
  const location = useLocation();
  const navigate = useNavigate();

  // Ask built or opened a dashboard (TRO-469): from the side panel, the report
  // becomes the page beside the conversation, following the run's own event
  // stream (?run=) so cards appear as they are added.
  useEffect(() => {
    const onDashboard = (e) => {
      const d = (e && e.detail) || {};
      if (!['created', 'opened'].includes(d.op) || !d.dashboard_id) return;
      const team = window.location.pathname.split('/').filter(Boolean)[0];
      if (!team) return;
      const fromChat = /^\/[^/]+\/chat\/?$/.test(window.location.pathname);
      // On the full chat page a report being built stays in the conversation:
      // the answer draws its cards (ReportCard), and leaving mid-run lost the
      // live trail. Asked to open one, it opens beside the chat like any link.
      if (fromChat) {
        if (d.op === 'opened') window.dispatchEvent(new CustomEvent('trodo:openBeside', { detail: { path: `/${team}/reports/${d.dashboard_id}` } }));
        return;
      }
      const onIt = window.location.pathname.endsWith(`/reports/${d.dashboard_id}`) || window.location.pathname.endsWith('/overview');
      if (!onIt) navigate(`/${team}/reports/${d.dashboard_id}${d.run_id ? `?run=${encodeURIComponent(d.run_id)}` : ''}`);
      setAskOpen(true);
    };
    // Any other page Lucid links to opens the same way: the page as the main
    // view, the conversation beside it.
    const onBeside = (e) => {
      const path = e?.detail?.path;
      if (!path || typeof path !== 'string' || !path.startsWith('/')) return;
      const fromChat = /^\/[^/]+\/chat\/?$/.test(window.location.pathname);
      const sessionId = fromChat ? new URLSearchParams(window.location.search).get('session') : null;
      // From the full chat page the conversation moves to the side panel; from
      // the side panel it is already there; from any other page it is a link.
      // Handed over through sessionStorage too: the chat page and most others
      // are separate routes, each mounting its own shell, and state set here
      // was lost with this one (the page opened, the conversation did not).
      if (fromChat) {
        try { sessionStorage.setItem(ASK_BESIDE_KEY, JSON.stringify({ sessionId, at: Date.now() })); } catch { /* private mode */ }
        if (sessionId) setAskResume({ sessionId, nonce: Date.now() });
        setAskOpen(true);
      }
      navigate(path);
    };
    window.addEventListener('trodo:dashboard', onDashboard);
    window.addEventListener('trodo:openBeside', onBeside);
    return () => {
      window.removeEventListener('trodo:dashboard', onDashboard);
      window.removeEventListener('trodo:openBeside', onBeside);
    };
  }, [navigate]);
  const { activated: teamActivated, pending: activationPending } = useTeamActivation();
  const [selectedTeam, setSelectedTeam] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("selectedTeam") || "";
    }
    return "";
  });
  // Simple Project Nova Design System styles
  const styles = {
    primaryColor: primary,
    secondaryColor: "#caa968", // Muted Gold
    backgroundColor: "var(--surface-sunken)", // Light gray background
    textColor: "var(--text-primary)",
    textHeaders: primary,
    borderColor: "rgba(255, 255, 255, 0.18)", // Glass border
    glassEffect: "rgba(255, 255, 255, 0.7)",
    glassBorder: "rgba(255, 255, 255, 0.18)",
    cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
  };
  
  // Screen size detection with multiple breakpoints.
  // IMPORTANT: never depend on `isSidebarOpen` here — doing so causes the
  // effect to re-fire every time the user toggles the drawer on mobile,
  // which then immediately closes it again (race that made the hamburger
  // appear broken). Sidebar open/close is driven by user clicks only on
  // mobile; we only auto-open on the desktop breakpoint transition.
  useEffect(() => {
    let lastWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const applyDefaultOpenState = (width) => {
      setIsSidebarOpen(width >= 1024);
    };
    applyDefaultOpenState(lastWidth);

    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024
      });
      const crossedDesktopBoundary = (lastWidth < 1024) !== (width < 1024);
      if (crossedDesktopBoundary) applyDefaultOpenState(width);
      lastWidth = width;
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Sync selectedPage with URL
  useEffect(() => {
    setIsPageLoading(true); // Start loading when URL changes
    
    const path = location.pathname;
    const pathSegments = path.split('/').filter(Boolean);
    
    // Handle nested routes properly
    let currentRoute = '';
    let subRoute = '';
    if (pathSegments.length > 1) {
      currentRoute = pathSegments[1];
    }
    if (pathSegments.length > 2) {
      subRoute = pathSegments[2];
    }
    
    if (currentRoute === 'agent-analytics') {
      const sub = subRoute && AGENT_ANALYTICS_SUBPAGES.has(subRoute) ? subRoute : 'overview';
      setSelectedPage(`agent-analytics-${sub}`);
    } else {
      const routeToPageMap = {
        'home': 'home',
        'chat': 'chat',
        'search': 'search',
        'activity': 'activity',
        'events': 'activity',
        'integrations': 'integrations',
        'inbox': 'inbox',
        'agents': 'agents',
        'alerts': 'alerts',
        'playground': 'playground',
        'prompts': 'prompts',
        'datasets': 'datasets',
        'reports': 'reports',
        'overview': 'overview',
        'settings': 'settings',
        'user-settings': 'user-settings'
      };
      
      const newPage = routeToPageMap[currentRoute] || 'dashboard';
      setSelectedPage(newPage);
    }

    // Small delay to prevent flash, then set loading to false
    setTimeout(() => {
      setIsPageLoading(false);
    }, 100);
    // pathname only: `location` object identity can change every render in react-router v7,
    // which would re-run this effect and overwrite optimistic setSelectedPage from nav clicks.
  }, [location.pathname]);

  // Invalid /:team/agent-analytics/:subpage → overview, except the retired Heal
  // page, which goes to Issues. Healing lives on the issue now, so a bookmark or
  // a Slack link from before the move should land where the feature went rather
  // than on an unrelated page.
  useEffect(() => {
    const segs = location.pathname.split('/').filter(Boolean);
    if (segs[1] !== 'agent-analytics' || !segs[2]) return;
    if (AGENT_ANALYTICS_SUBPAGES.has(segs[2])) return;
    const to = segs[2] === 'heal' ? 'agent-analytics/issues?tab=issues' : 'overview';
    navigate(`/${encodeURIComponent(segs[0])}/${to}`, { replace: true });
  }, [location.pathname, navigate]);

  // Keep team state aligned with the URL so navigation never uses an empty slug (which would produce `//chat` and break routing).
  useEffect(() => {
    if (!teamIdFromUrl) return;
    setSelectedTeam((prev) => (prev === teamIdFromUrl ? prev : teamIdFromUrl));
  }, [teamIdFromUrl]);

  const handleNavigation = (page) => {
    const rawTeam =
      teamIdFromUrl ||
      (selectedTeam && selectedTeam.trim()) ||
      (typeof window !== "undefined" ? localStorage.getItem("selectedTeam")?.trim() : "") ||
      "";
    if (!rawTeam) return;
    const slug = encodeURIComponent(rawTeam);
    if (page === "dashboard") {
      navigate(`/${slug}/chat`);
      return;
    }
    if (page === "home") {
      navigate(`/${slug}/chat`);
      return;
    }
    if (page === "analytics-reports") {
      navigate(LEGACY_STUDIO_UI ? `/${slug}/analytics/reports` : `/${slug}/reports`);
      return;
    }

    setIsPageLoading(true); // Show loading when navigating
    setSelectedPage(page);
    navigate(`/${slug}/${page}`);
  };

  // Get sidebar classes based on screen size and state
  const getSidebarClasses = () => {
    // Rail at z-50 stays above the main column (no z-index) so nav clicks win in any overlap.
    // Main must not use z-* here: a stacking context on main would trap fixed modals/drawers below
    // the rail so the dimmer never covers the nav. Mobile drawer backdrop is z-40 — rail stays above it.
    return "h-screen sticky top-0 flex-shrink-0 visible relative z-50";
  };

  // Get main content classes - optimized for performance
  const getMainContentClasses = () => {
    const fullBleed = location.pathname.includes('/agent-analytics/runs')
      || location.pathname.includes('/agent-analytics/clusters')
      || location.pathname.includes('/agent-analytics/use-cases')
      || location.pathname.includes('/agent-analytics/capabilities')
      || location.pathname.includes('/agent-analytics/issues')
      || location.pathname.includes('/agent-analytics/evals')
      || location.pathname.includes('/agents')
      || location.pathname.includes('/playground')
      || location.pathname.includes('/prompts')
      || location.pathname.includes('/datasets')
      || location.pathname.includes('/reports')
      || location.pathname.includes('/overview')
      || location.pathname.includes('/search');
    let baseClasses = fullBleed
      ? 'flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden relative transition-transform duration-200 ease-out '
      : 'flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto relative transition-transform duration-200 ease-out ';
    if (screenSize.isDesktop) {
        baseClasses += isSidebarOpen ? "ml-0" : "ml-0";
    }
    return baseClasses;
  };

  // Get main padding classes
  const getMainPaddingClasses = () => {
    return "p-3 sm:p-4 md:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-8 min-w-0";
  };

  // Render component for current page
  const renderCurrentPage = () => {
    const teamSlug = encodeURIComponent(teamIdFromUrl || selectedTeam);

    // ── Before the first trace arrives ──────────────────────────────────────
    // Every data page shows the install flow instead of an empty dashboard,
    // but the shell — sidebar, team switcher, settings — stays usable. This
    // replaced a full-screen install wall that hid the sidebar entirely, which
    // meant a team could not invite a teammate to do the instrumentation or
    // upgrade to Pro until after tracing was already live.
    //
    // Settings is exempt for exactly that reason. `pending` holds a skeleton
    // rather than guessing: guessing wrong flashes the install panel at a team
    // that has been sending traces for months.
    if (!INSTALL_EXEMPT_PAGES.has(selectedPage)) {
      if (activationPending) {
        return <main className={getMainPaddingClasses()}><PageLoader label="Loading your workspace" /></main>;
      }
      if (!teamActivated) {
        return (
          <main className={getMainPaddingClasses()}>
            <InstallPanel onActivated={() => navigate(`/${teamSlug}/chat`, { replace: true })} />
          </main>
        );
      }
    }

    const commonProps = {
      onMenuClick: selectedPage === "settings"
        ? () => {}
        : () => setIsSidebarOpen(!isSidebarOpen),
      onClose: () => navigate(`/${teamSlug}/chat`),
      screenSize: screenSize,
      selectedPage: selectedPage,
      isSidebarOpen: isSidebarOpen
    };

    const withSubscriptionCheck = (component, featureName, requiredPage = null) => {
      // Always allow dashboard, settings, and user-settings pages without subscription check
      if (selectedPage === 'settings' || selectedPage === 'user-settings' || selectedPage === 'inbox') {
        return component;
      }
      
      // For premium pages, wrap with SubscriptionRequired using centralized access control
      return <SubscriptionRequired featureName={featureName} requiredPage={requiredPage}>{component}</SubscriptionRequired>;
    };

    switch (selectedPage) {
      case "dashboard":
        // Must not Navigate on first paint: selectedPage defaults to "dashboard" before the
        // location sync effect runs, which would instantly replace /activity/* with home.
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
              </main>
            </>
          );
        }
        return (
          <Navigate
            to={`/${encodeURIComponent(teamIdFromUrl || selectedTeam)}/chat`}
            replace
          />
        );
      case "activity":
        if (params.groupKey && params.groupId) {
          return withSubscriptionCheck(
            <GroupDetailPage {...commonProps} />,
            "activity analytics",
            "activity"
          );
        }
        // A user is a drawer now, opened in place from anywhere; the old
        // full-page URL opens the list with that user's drawer.
        if (params.distinctId) return <UserProfileRoute />;
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <ActivityPage {...commonProps} />,
          "activity analytics",
          "activity"
        );
      case "agent-analytics-overview":
        // The Overview is the team's default dashboard now (TRO-470).
        return <Navigate to={`/${encodeURIComponent(selectedTeam || teamIdFromUrl || '')}/overview${window.location.search}`} replace />;

      case "agent-analytics-runs":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return withSubscriptionCheck(
          <AgentRunsPage {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agent-analytics-conversations":
        return withSubscriptionCheck(
          <ConversationsPage {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agent-analytics-traces":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return withSubscriptionCheck(
          <TracesPage {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agent-analytics-clusters":
      case "agent-analytics-use-cases":
        // Legacy routes — redirect to the renamed Capabilities page in-place.
        // Using <Navigate replace> so a fresh page load at /clusters or
        // /use-cases transparently lands on /capabilities without leaving the
        // old URL in the browser back-stack.
        return (
          <Navigate
            to={`/${encodeURIComponent(selectedTeam || teamIdFromUrl || '')}/agent-analytics/capabilities`}
            replace
          />
        );

      case "agent-analytics-capabilities":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        // Capabilities renders the capability model (the density-clustering
        // UseCasesPage/ClusterDirectoryPage were removed).
        return withSubscriptionCheck(
          <CapabilitiesPage {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agent-analytics-issues":
      case "agent-analytics-issues-catalog":
        // The pattern catalog (the -catalog page) went with the detectors; an old
        // link to it lands on Issues. ISSUES_UI_ENABLED off redirects to Overview.
        if (!ISSUES_UI_ENABLED) {
          return (
            <Navigate
              to={`/${encodeURIComponent(selectedTeam || teamIdFromUrl || '')}/overview`}
              replace
            />
          );
        }
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return withSubscriptionCheck(
          <IssuesPage {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agent-analytics-evals":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return withSubscriptionCheck(
          <Evals3Page {...commonProps} />,
          "agent analytics",
          "activity"
        );

      case "agents":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            {/*
              Key on pathname so navigating list ↔ editor ↔ executions forces a
              clean unmount. Without this the workflow editor (with its ReactFlow
              + ReactFlowProvider) can stay mounted after the URL changes,
              leaving the URL bar pointing at /agents while the canvas is still
              on screen.
            */}
            <AgentsPage key={location.pathname} />
          </main>
        );
      case "alerts":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <AlertsPage key={location.pathname} />
          </main>
        );
      case "playground":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            {/* A draft saved as a playground moves from /new to /<id> with
                state.adoptedDraft: same key, so the editor (and a run the save
                started) stays mounted. Any other move remounts it. */}
            <PlaygroundPage key={location.state?.adoptedDraft ? location.pathname.replace(/[^/]+\/?$/, 'new') : location.pathname} />
          </main>
        );
      case "prompts":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <PromptsPage key={location.pathname} />
          </main>
        );
      case "reports":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <DashboardsPage />
          </main>
        );
      case "overview":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <OverviewPage />
          </main>
        );
      case "datasets":
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <DatasetsPage key={location.pathname} />
          </main>
        );
      // "integrations" has no case: the page moved to Settings > Project >
      // Integrations, and /:team/integrations now redirects there (App.js).
      case "inbox":
        if (isPageLoading) {
          return (
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
            </main>
          );
        }
        return (
          <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0">
            <InboxPage />
          </main>
        );
      case "home":
        return (
          <Navigate
            to={`/${encodeURIComponent(teamIdFromUrl || selectedTeam)}/chat`}
            replace
          />
        );
      case "chat":
        return (
          <main
            className={`flex flex-1 flex-col min-h-0 overflow-hidden ${
              ""
            }`}
          >
            <ChatPage />
          </main>
        );
      case "search":
        // Semantic search lives in features/search/ — that directory is
        // untracked locally and is the user's WIP, so we render a
        // placeholder until the feature lands cleanly. The case is kept
        // so the route doesn't 404 in the meantime.
        return (
          <main
            className={`flex flex-1 flex-col min-h-0 overflow-hidden ${
              ""
            }`}
          >
            <div className="p-8 text-center text-sm text-zinc-500">
              Semantic search is coming soon.
            </div>
          </main>
        );
      case "settings":
        return <Settings />;
      case "user-settings":
        return (
          <main className={getMainPaddingClasses()}>
            <UserSettings />
          </main>
        );
      default:
        // Show loading spinner if page is still being determined, otherwise show 404
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                <PageLoader label={PAGE_LOADING_LABEL[selectedPage] || "Loading"} />
              </main>
            </>
          );
        }
        return (
          <>
            <main className={getMainPaddingClasses()}>
              <div className="text-center py-12">
                <h2 className="text-2xl font-semibold text-[color:var(--text-secondary)]">Page Not Found</h2>
                <p className="mt-2 text-[color:var(--text-secondary)]">The page you're looking for doesn't exist.</p>
              </div>
            </main>
          </>
        );
    }
  };

  const teamId = teamIdFromUrl || selectedTeam;

  return (
    <PinnedBoardsProvider teamId={teamId || ""}>
    <BreadcrumbProvider>
    <div className="flex h-screen overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Body: sidebar (full height) + right column (top bar + content) */}
      <div className="flex flex-1 min-h-0 overflow-hidden bg-[color:var(--surface-sunken)]">

        {/* Sidebar: full height, no top bar above it. Its rendered width is
            published as --trodo-rail-w so drawer overlays can stop at its edge. */}
        <div className={getSidebarClasses()} ref={sidebarRef}>
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            onNavigate={handleNavigation}
            currentPage={selectedPage}
            isCompact={true}
            screenSize={screenSize}
          />
        </div>

        {/* Right column: a small grey strip on top (pt) connects to the sidebar
            grey, isolating the white main panel below. The path bar lives INSIDE
            the white panel (same colour as the page). */}
        <div className="flex flex-1 min-w-0 flex-col overflow-hidden bg-[color:var(--surface-sunken)] pt-2">
          {/* White panel: relative anchor for drawers, clips the rounded corner */}
          <div className="relative flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden bg-surface rounded-tl-2xl border-t border-l border-[color:var(--border-default)]">
            {/* Single scroll container: path bar + page scroll together */}
            <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto">
              <GlobalTopBar
                onMobileMenuToggle={(screenSize.isMobile || screenSize.isTablet) ? () => setIsSidebarOpen((v) => !v) : undefined}
                isMobileMenuOpen={isSidebarOpen}
              />
              {renderCurrentPage()}
            </div>
            {/* The user drawer, opened by openUserProfile() from any page. */}
            <UserProfileHost />
          </div>
        </div>

        {/* Ask, beside the page rather than over it. A flex sibling of the
            content column, so the page shrinks and both stay visible. Never on
            the chat page: there the page IS the conversation. */}
        {askOpen && selectedPage !== 'chat' && (
          <AskPanel open draft={askDraft} resume={askResume} onClose={() => setAskOpen(false)} />
        )}

        {/* Where a page can stand a panel beside the whole content column
            rather than inside its own body: the same box Ask occupies, so it
            runs the full height of the panel instead of starting under the
            path bar. The chat page's conversation list portals in here.
            `empty:hidden` so it costs nothing when no page is using it. */}
        <div
          id={SIDE_PANEL_SLOT_ID}
          className="flex shrink-0 flex-col overflow-hidden bg-[color:var(--surface-sunken)] pt-2 empty:hidden"
        />

      </div>

      {(screenSize.isMobile || screenSize.isTablet) && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-[color:var(--surface-overlay)] z-40 transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
    </BreadcrumbProvider>
    </PinnedBoardsProvider>
  );
};

export default Dashboard;
